import { createElement, Fragment, type ReactNode } from "react";
import type { IUpsellViewData } from "@features/shared/entities/student";

type ViewDataVariables = Record<string, string | number | null | undefined>;

/**
 * Resolves `{{name}}` placeholders in an admin-authored view_data string.
 *
 * Admin copy is served already localized, so it deliberately does NOT go
 * through `t()` — passing it there would look up the authored text as a
 * natural key and could re-translate it into the UI language, which may differ
 * from the content locale. The built-in English defaults still go through
 * `t()` and get i18next's own interpolation; this helper gives the override the
 * same placeholder support without that round trip.
 *
 * Unknown placeholders are left as-is so a typo stays visible in the editor
 * preview instead of silently rendering an empty string.
 */
export const applyViewDataVariables = (text: string, variables?: ViewDataVariables): string => {
    if (!variables) return text;
    return text.replace(/{{\s*(\w+)\s*}}/g, (match, name: string) => {
        const value = variables[name];
        return value === null || value === undefined ? match : String(value);
    });
};

// Same marker set as the adaptive funnel builder (afb-core.js RICH_RE):
// **bold**, ==accent== (gradient) and --primary-- (solid colour). Funnels
// store the parsed parts in their config; upsell view_data is contractually
// flat strings, so the markers stay in the string and are parsed at render.
const RICH_RE = /\*\*([\s\S]+?)\*\*|==([\s\S]+?)==|--([\s\S]+?)--/g;
const BOLD_CLASS = "font-bold";
const ACCENT_CLASS =
    "bg-[linear-gradient(180deg,var(--color-blue-600),var(--color-blue-500))] bg-clip-text text-transparent";
const PRIMARY_CLASS = "text-blue-600";

/**
 * Renders an admin-authored view_data string with the funnel builder's style
 * markers. Applied ONLY to overrides — the built-in `t()` defaults never pass
 * through here, so original copy (and its translations) cannot be reparsed.
 * `undefined` in, `undefined` out, which keeps `richText(viewData?.x) ?? t(…)`
 * fallback chains working; a marker-less string comes back unchanged.
 */
export const richText = (text: string | undefined): ReactNode | undefined => {
    if (text === undefined) return undefined;
    RICH_RE.lastIndex = 0;
    if (!RICH_RE.test(text)) return text;
    RICH_RE.lastIndex = 0;
    const parts: ReactNode[] = [];
    let last = 0;
    let key = 0;
    let m: RegExpExecArray | null;
    while ((m = RICH_RE.exec(text)) !== null) {
        if (m.index > last) parts.push(text.slice(last, m.index));
        const cls = m[1] != null ? BOLD_CLASS : m[2] != null ? ACCENT_CLASS : PRIMARY_CLASS;
        parts.push(createElement("span", { key: key++, className: cls }, m[1] ?? m[2] ?? m[3]));
        last = RICH_RE.lastIndex;
    }
    if (last < text.length) parts.push(text.slice(last));
    return createElement(Fragment, null, parts);
};

/**
 * Marker parsing for override strings that are rendered via
 * dangerouslySetInnerHTML (fields that already accept raw HTML like <b>/<br>):
 * same markers, but emitted as HTML instead of React nodes. Apply ONLY to the
 * admin override branch — never to a t() default.
 */
export const richTextHtml = (text: string): string =>
    text.replace(RICH_RE, (_, bold, accent, primary) => {
        const cls = bold != null ? BOLD_CLASS : accent != null ? ACCENT_CLASS : PRIMARY_CLASS;
        return `<span class="${cls}">${bold ?? accent ?? primary}</span>`;
    });

/**
 * Applies `{{name}}` substitutions to every string value of a view_data blob.
 * Used once per screen (see the resolved store in features/upsell/model), so
 * every text field supports the standard variables without per-field wiring;
 * unknown placeholders stay visible, exactly like applyViewDataVariables.
 */
export const substituteViewDataVariables = (
    viewData: IUpsellViewData | null,
    variables: ViewDataVariables
): IUpsellViewData | null => {
    if (!viewData) return viewData;
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(viewData)) {
        out[key] = typeof value === "string" ? applyViewDataVariables(value, variables) : value;
    }
    return out as IUpsellViewData;
};
